import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { StorageService } from 'ng-blockchainx';
import { environment } from 'environments/environment';
import { AuthService } from 'app/core/auth/auth.service';

@Injectable()
export class AccessTokenInterceptor implements HttpInterceptor {
    /**
     * `AuthService` and `Router` are read lazily through `Injector`, never injected directly:
     * `AuthService` depends on `HttpClient`, and injecting it into an HTTP interceptor builds a
     * cyclic dependency that fails bootstrap. Resolving them inside the error handler, after
     * construction, breaks that cycle.
     */
    constructor(private storage: StorageService, private injector: Injector) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const accessToken = this.storage.get('accessToken');
        // add auth header with access token if account is logged in and request is to the api url
        const isApiUrl = request.url.startsWith(environment.API_BASE_URL);
        // The upload endpoint sits outside API_BASE_URL. It gets the token but is deliberately left
        // out of the 401 sign-out below: its backend guard (`userAuth.check`) rejects admin sessions,
        // so a 401 there does not mean the admin session has expired.
        const isUploadUrl = request.url.startsWith(environment.FILE_UPLOAD_URL);
        if (accessToken && (isApiUrl || isUploadUrl)) {
            request = request.clone({
                setHeaders: { Authorization: `Bearer ${accessToken}` }
            });
        }
        return next.handle(request).pipe(
            catchError((error) => {
                // A 401 on an authenticated API call means the backend session has expired or been
                // revoked. Before this, the stale localStorage session survived: pages still opened
                // but every call failed and nothing redirected (docs/troubleshooting.md). Sign out
                // through the one AuthService path (keeps the device id) and go to sign-in.
                // Guarded by `accessToken` so a failed sign-in (no token yet) and third-party calls
                // are left exactly as they were.
                if (error instanceof HttpErrorResponse && error.status === 401 && isApiUrl && accessToken) {
                    this.injector.get(AuthService).signOut();
                    this.injector.get(Router).navigate(['sign-in']);
                }
                return throwError(error);
            })
        );
    }
}
